import './auth.css';
import { supabase } from '../lib/supabase';
import { escapeHtml, toast } from '../lib/ui';

type AuthMode = 'login' | 'signup' | 'reset';

// מינימום אורך סיסמה — חייב להיות זהה ל-password_min_length של פרויקט ה-Supabase.
// ברכזת המשותפת הוא 8; אם הלקוח יבדוק פחות, השרת ידחה והמשתמשת תקבל שגיאה גנרית.
const MIN_PASSWORD = 8;

// כתובת הנחיתה של קישורי מייל. חובה מפורשת: site_url של הפרויקט שייך לאפליקציה
// אחרת ברכזת, ובלי הפרמטר הזה קישור איפוס של LiteBite נוחת אצלה.
const redirectTo = (): string => window.location.origin;

// מסכי הכניסה/הרשמה/איפוס — מוחלפים בתוך אותו כרטיס לפי mode.
export function renderAuth(): string {
  return `
    <div class="auth-wrap">
      <div class="auth-logo">🌿</div>
      <h1 class="auth-title"><span>Lite</span>Bite</h1>
      <p class="auth-sub">יומן תזונה שנעים לחזור אליו</p>
      <div class="card auth-card" id="auth-card"></div>
    </div>`;
}

function formHtml(mode: AuthMode): string {
  if (mode === 'reset') {
    return `
      <h2>איפוס סיסמה</h2>
      <label>אימייל<input type="email" id="auth-email" autocomplete="email" dir="ltr"></label>
      <button class="btn-primary" id="auth-submit">שלחי קישור איפוס</button>
      <button class="btn-link" data-mode="login">חזרה לכניסה</button>`;
  }
  const isSignup = mode === 'signup';
  return `
    <h2>${isSignup ? 'יצירת חשבון' : 'כניסה'}</h2>
    <label>אימייל<input type="email" id="auth-email" autocomplete="email" dir="ltr"></label>
    <label>סיסמה<input type="password" id="auth-password" autocomplete="${isSignup ? 'new-password' : 'current-password'}" dir="ltr"></label>
    <button class="btn-primary" id="auth-submit">${isSignup ? 'הרשמה' : 'כניסה'}</button>
    ${isSignup
      ? '<button class="btn-link" data-mode="login">יש לך כבר חשבון? כניסה</button>'
      : `<button class="btn-link" data-mode="signup">אין לך חשבון? הרשמה</button>
         <button class="btn-link" data-mode="reset">שכחת סיסמה?</button>`}
    <div class="auth-error" id="auth-error"></div>`;
}

// הרשמה שדורשת אישור מייל: המשתמשת נוצרה אבל אין סשן, ואסור להיכנס לאפליקציה.
function confirmSentHtml(email: string): string {
  return `
    <h2>כמעט שם</h2>
    <p class="auth-note">שלחנו קישור אישור ל־<span dir="ltr">${escapeHtml(email)}</span>.<br>
    אחרי האישור אפשר להיכנס — ובר יאשר את החשבון.</p>
    <button class="btn-link" data-mode="login">חזרה לכניסה</button>`;
}

export function attachAuth(onAuthed: () => void, notice?: string): void {
  const card = document.getElementById('auth-card')!;
  let mode: AuthMode = 'login';
  let busy = false;
  // הודעה חד-פעמית מנחיתת קישור מייל שנכשלה (פג תוקף / כבר נוצל).
  let pendingNotice = notice;

  const showError = (msg: string) => {
    const el = document.getElementById('auth-error');
    if (el) el.textContent = msg;
  };

  const render = () => {
    card.innerHTML = formHtml(mode);
    if (pendingNotice) {
      showError(pendingNotice);
      pendingNotice = undefined;
    }
    card.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((btn) => {
      btn.addEventListener('click', () => {
        mode = btn.dataset.mode as AuthMode;
        render();
      });
    });
    card.querySelector('#auth-submit')!.addEventListener('click', submit);
    card.querySelectorAll('input').forEach((inp) =>
      inp.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') submit();
      }),
    );
  };

  const submit = async () => {
    if (busy) return;
    const email = (document.getElementById('auth-email') as HTMLInputElement).value.trim();
    const password = (document.getElementById('auth-password') as HTMLInputElement | null)?.value ?? '';
    if (!email) return showError('צריך אימייל');
    if (mode !== 'reset' && password.length < MIN_PASSWORD) {
      return showError(`סיסמה — לפחות ${MIN_PASSWORD} תווים`);
    }

    busy = true;
    const btn = document.getElementById('auth-submit') as HTMLButtonElement;
    btn.disabled = true;
    try {
      if (mode === 'reset') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectTo() });
        if (error) throw error;
        toast('קישור איפוס נשלח למייל');
        mode = 'login';
        render();
      } else if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email, password, options: { emailRedirectTo: redirectTo() },
        });
        if (error) throw error;
        // כשאישור המייל נדרש, signUp מחזיר משתמש בלי סשן — אסור להיכנס לאפליקציה.
        if (!data.session) {
          card.innerHTML = confirmSentHtml(email);
          card.querySelector('[data-mode]')!.addEventListener('click', () => { mode = 'login'; render(); });
          return;
        }
        onAuthed();
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onAuthed();
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      showError(
        /invalid login credentials/i.test(msg) ? 'אימייל או סיסמה שגויים'
        : /already registered/i.test(msg) ? 'האימייל כבר רשום — נסי להיכנס'
        : /email not confirmed/i.test(msg) ? 'החשבון עדיין לא אושר במייל — פני לבר'
        : /password.*(at least|characters|weak)/i.test(msg) ? `סיסמה חלשה מדי — לפחות ${MIN_PASSWORD} תווים`
        : /signups? not allowed/i.test(msg) ? 'ההרשמה סגורה כרגע — בקשי מבר לפתוח חשבון'
        : 'משהו השתבש — נסי שוב',
      );
    } finally {
      busy = false;
      const b = document.getElementById('auth-submit') as HTMLButtonElement | null;
      if (b) b.disabled = false;
    }
  };

  render();
}

// מסך קביעת סיסמה חדשה — נחיתה מקישור השחזור.
// supabase-js כבר צרך את הטוקן מה-URL ויצר סשן זמני, ולכן updateUser עובד כאן.
export function renderRecovery(): string {
  return `
    <div class="auth-wrap">
      <div class="auth-logo">🔑</div>
      <h1 class="auth-title">סיסמה חדשה</h1>
      <p class="auth-sub">בחרי סיסמה חדשה לחשבון</p>
      <div class="card auth-card">
        <label>סיסמה חדשה<input type="password" id="rec-password" autocomplete="new-password" dir="ltr"></label>
        <label>שוב, לוודא<input type="password" id="rec-confirm" autocomplete="new-password" dir="ltr"></label>
        <button class="btn-primary" id="rec-submit">שמירה וכניסה</button>
        <button class="btn-link" id="rec-cancel">חזרה לכניסה</button>
        <div class="auth-error" id="auth-error"></div>
      </div>
    </div>`;
}

export function attachRecovery(onDone: () => void, onCancel: () => void): void {
  const showError = (msg: string) => {
    const el = document.getElementById('auth-error');
    if (el) el.textContent = msg;
  };
  const btn = document.getElementById('rec-submit') as HTMLButtonElement;
  let busy = false;

  const submit = async () => {
    if (busy) return;
    const password = (document.getElementById('rec-password') as HTMLInputElement).value;
    const confirm = (document.getElementById('rec-confirm') as HTMLInputElement).value;
    if (password.length < MIN_PASSWORD) return showError(`סיסמה — לפחות ${MIN_PASSWORD} תווים`);
    if (password !== confirm) return showError('שתי הסיסמאות לא זהות');

    busy = true;
    btn.disabled = true;
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      // מנקים את טוקן השחזור מה-URL כדי שרענון לא יחזיר את המסך הזה.
      history.replaceState(null, '', window.location.pathname);
      toast('הסיסמה עודכנה');
      onDone();
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      showError(
        /password.*(at least|characters|weak)/i.test(msg) ? `סיסמה חלשה מדי — לפחות ${MIN_PASSWORD} תווים`
        // הניסוח בפועל של GoTrue: "New password should be different from the old password."
        : /different from the old password|same.?password/i.test(msg) ? 'זו הסיסמה הנוכחית — בחרי אחרת'
        : /expired|invalid/i.test(msg) ? 'הקישור פג תוקף — בקשי קישור חדש'
        : 'משהו השתבש — נסי שוב',
      );
    } finally {
      busy = false;
      btn.disabled = false;
    }
  };

  btn.addEventListener('click', submit);
  document.getElementById('rec-cancel')!.addEventListener('click', onCancel);
  document.querySelectorAll('#rec-password, #rec-confirm').forEach((inp) =>
    inp.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') void submit();
    }),
  );
}

// מסך "ממתין לאישור" — נרשם חדש עד שבר מאשר אותו.
export function renderPending(): string {
  return `
    <div class="auth-wrap">
      <div class="auth-logo">🌱</div>
      <h1 class="auth-title">כמעט שם!</h1>
      <p class="auth-sub">החשבון נוצר וממתין לאישור המנהל.<br>ברגע שיאושר — אפשר להתחיל.</p>
      <div class="pending-actions">
        <button class="btn-primary" id="pending-refresh">בדקי שוב</button>
        <button class="btn-link" id="pending-logout">התנתקות</button>
      </div>
    </div>`;
}

// מסך שגיאת רשת בעליית האפליקציה — לא מסיקים כלום על הרשאות, רק מציעים ניסיון חוזר.
export function renderBootError(): string {
  return `
    <div class="auth-wrap">
      <div class="auth-logo">🍃</div>
      <h1 class="auth-title">אין חיבור כרגע</h1>
      <p class="auth-sub">לא הצלחנו לבדוק את החשבון. בדקי את החיבור ונסי שוב.</p>
      <div class="pending-actions">
        <button class="btn-primary" id="pending-refresh">ניסיון חוזר</button>
        <button class="btn-link" id="pending-logout">התנתקות</button>
      </div>
    </div>`;
}

export function attachPending(onRefresh: () => void): void {
  document.getElementById('pending-refresh')!.addEventListener('click', onRefresh);
  document.getElementById('pending-logout')!.addEventListener('click', async () => {
    await supabase.auth.signOut();
  });
}
